import { useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useGame } from "../context/GameContext.jsx";
import { useLanguage } from "../hooks/useLanguage.js";
import { fadeIn } from "../animations/variants.js";
import { ScrollNav } from "../components/ui/ScrollNav.jsx";
import { VillageScene } from "../components/village/VillageScene.jsx";
import {
  MayorElectionView, NightPhaseView, DayRevealView, HunterShotView,
  DebateView, VotingView, GameOverView,
} from "../components/phases/PhaseViews.jsx";

export function GameScreen({ onOpenDead }) {
  const { state } = useGame();
  const { t } = useLanguage();
  const session = state.session;
  const scrollRef = useRef(null);
  if (!session) return null;

  const phase = session.phase;
  const isNight = phase === "night" || phase === "night_resolution" || phase === "mayor_election" || phase === "hunter_shot";

  return (
    <motion.div variants={fadeIn} initial="initial" animate="animate" exit="exit"
      className={`min-h-screen flex flex-col transition-colors duration-2000
        ${isNight ? "bg-gradient-to-b from-indigo-950 via-slate-950 to-stone-950"
                  : "bg-gradient-to-b from-stone-950 via-amber-950 to-stone-950"}`}>

      <div className="px-4 pt-10 pb-2">
        <VillageScene isNight={isNight} players={session.players} onOpenDead={onOpenDead} />
        <div className="flex gap-3 mt-2 text-xs text-center">
          <div className="flex-1 bg-emerald-950/40 border border-emerald-900/20 rounded-xl py-1.5">
            <p className="text-emerald-500/60">{t("Village", "Village")}</p>
            <p className="text-emerald-300 font-bold">{session.players.filter(p => p.alive && p.effectiveAlignment === "village").length}</p>
          </div>
          <div className="flex-1 bg-red-950/40 border border-red-900/20 rounded-xl py-1.5">
            <p className="text-red-500/60">{t("Loups", "Wolves")}</p>
            <p className="text-red-300 font-bold">{session.players.filter(p => p.alive && p.effectiveAlignment === "werewolf").length}</p>
          </div>
          <div className="flex-1 bg-stone-900/40 border border-stone-800/20 rounded-xl py-1.5">
            <p className="text-stone-500/60">{t("Morts", "Dead")}</p>
            <p className="text-stone-400 font-bold">{session.players.filter(p => !p.alive).length}</p>
          </div>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        <AnimatePresence mode="wait">
          {phase === "mayor_election" && <MayorElectionView key="mayor" />}
          {(phase === "night" || phase === "night_resolution") && <NightPhaseView key="night" />}
          {phase === "day_reveal" && <DayRevealView key="dayreveal" />}
          {phase === "hunter_shot" && <HunterShotView key="hunter" />}
          {phase === "debate" && <DebateView key="debate" />}
          {phase === "voting" && <VotingView key="voting" />}
          {phase === "game_over" && <GameOverView key="gameover" />}
        </AnimatePresence>
      </div>

      <ScrollNav scrollRef={scrollRef} />

      {phase !== "game_over" && (
        <div className="fixed bottom-safe left-4 right-4 bottom-4 pointer-events-none">
          <div className="flex justify-center">
            <div className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-semibold
              ${isNight ? "bg-indigo-950/80 border border-indigo-800/30 text-indigo-400"
                        : "bg-amber-950/80 border border-amber-800/30 text-amber-600"}`}>
              {phase === "mayor_election" ? t("Élection du Maire", "Mayor Election")
               : phase === "night" ? t(`Nuit ${session.night}`, `Night ${session.night}`)
               : phase === "night_resolution" ? t("Résolution de la nuit", "Night resolution")
               : phase === "day_reveal" ? t(`Jour ${session.day}`, `Day ${session.day}`)
               : phase === "hunter_shot" ? t("Tir du Chasseur", "Hunter's Shot")
               : phase === "debate" ? t("Débat", "Debate")
               : phase === "voting" ? t("Vote", "Voting")
               : ""}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}
